<template>
  <div class="vimeo-video-field">
    <!-- URL input: no video loaded yet -->
    <div v-if="!hasVideo" class="vimeo-video-field__url-input">
      <div class="d-flex align-center" style="gap: 8px">
        <TextField
          v-model="urlInput"
          placeholder="Paste Vimeo URL..."
          wide
          @keydown.native.enter.prevent="loadVideo"
        />
        <v-btn
          :loading="fetchingMetadata"
          :disabled="!parsedUrl || fetchingMetadata"
          @click="loadVideo"
          color="primary"
          outlined
          small
        >
          Load
        </v-btn>
      </div>
    </div>

    <!-- Video loaded: metadata + preview controls -->
    <div v-else class="vimeo-video-field__content">
      <div class="vimeo-video-field__info d-flex align-start" style="gap: 16px">
        <div v-if="thumbnail" class="vimeo-video-field__thumbnail-wrap">
          <img :src="thumbnail" class="vimeo-video-field__thumbnail" />
          <v-tooltip bottom max-width="220">
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="vimeo-video-field__thumbnail-info"
                x-small
              >mdi-information</v-icon>
            </template>
            <span>Poster is fetched from Vimeo. To change it, update the thumbnail in Vimeo, then click "Reload from Vimeo".</span>
          </v-tooltip>
        </div>
        <div class="vimeo-video-field__meta">
          <div class="vimeo-video-field__title font-weight-medium">
            <a
              v-if="localValue.link"
              :href="localValue.link"
              target="_blank"
              rel="noopener noreferrer"
              >{{ localValue.title }}</a
            >
            <span v-else>{{ localValue.title }}</span>
          </div>
          <div class="vimeo-video-field__duration caption grey--text">
            {{ formatDuration(localValue.duration) }}
            &nbsp;&bull;&nbsp; ID: {{ localValue.vimeo_id }}
            <span v-if="localValue.vimeo_h">/ {{ localValue.vimeo_h }}</span>
          </div>
          <div v-if="localValue.vimeo_loaded_at" class="caption grey--text">
            Loaded {{ formatLoadedAt(localValue.vimeo_loaded_at) }}
          </div>
          <div class="d-flex mt-2" style="gap: 8px">
            <v-btn
              x-small
              outlined
              :loading="fetchingMetadata"
              @click="reloadVideo"
            >
              Reload from Vimeo
            </v-btn>
            <v-btn x-small outlined @click="clearVideo"> Change Video </v-btn>
          </div>
        </div>
      </div>

      <!-- Preview Clip Section -->
      <div class="vimeo-video-field__preview-section mt-4">
        <div class="caption font-weight-medium mb-2">Preview Clip</div>

        <!-- Action buttons -->
        <div class="d-flex flex-wrap align-center mb-2" style="gap: 8px">
          <v-btn
            small
            outlined
            :loading="isGenerating"
            :disabled="!canGenerate"
            @click="generatePreviewClip"
          >
            {{
              localValue.preview_clip && previewClipStatus !== "custom"
                ? "Re-generate"
                : "Generate Preview Clip"
            }}
          </v-btn>

          <v-btn
            icon
            x-small
            :disabled="isGenerating"
            :color="showClipOptions ? 'primary' : ''"
            @click="showClipOptions = !showClipOptions"
          >
            <v-icon x-small>mdi-tune</v-icon>
          </v-btn>

          <v-btn
            v-if="previewClipStatus === 'custom' && hasEntity"
            small
            outlined
            @click="revertPreviewClip"
          >
            Revert to Generated
          </v-btn>

          <v-btn small outlined @click="showCustomSection = !showCustomSection">
            {{ showCustomSection ? "Cancel" : "Upload Custom" }}
          </v-btn>
        </div>

        <!-- Clip options -->
        <div
          v-if="showClipOptions"
          class="vimeo-video-field__clip-options mb-3"
        >
          <div class="d-flex align-center" style="gap: 16px">
            <div>
              <div class="caption grey--text mb-1">Clips</div>
              <v-btn-toggle v-model="clipCount" mandatory dense>
                <v-btn :value="2" x-small>2</v-btn>
                <v-btn :value="3" x-small>3</v-btn>
                <v-btn :value="4" x-small>4</v-btn>
                <v-btn :value="5" x-small>5</v-btn>
              </v-btn-toggle>
            </div>
            <div>
              <div class="caption grey--text mb-1">Duration (sec each)</div>
              <v-btn-toggle v-model="clipDuration" mandatory dense>
                <v-btn :value="5" x-small>5</v-btn>
                <v-btn :value="8" x-small>8</v-btn>
                <v-btn :value="10" x-small>10</v-btn>
                <v-btn :value="15" x-small>15</v-btn>
              </v-btn-toggle>
            </div>
          </div>
        </div>

        <!-- Generation status -->
        <div
          v-if="
            previewClipStatus === 'pending' ||
            previewClipStatus === 'processing'
          "
          class="d-flex align-center mb-3"
        >
          <v-progress-circular
            indeterminate
            size="14"
            width="2"
            color="primary"
            class="mr-2"
          />
          <span class="caption grey--text mr-3">
            {{
              previewClipStatus === "pending"
                ? "Queued..."
                : "Generating preview clip..."
            }}
          </span>
          <v-btn
            x-small
            text
            color="error"
            :loading="cancelling"
            @click="cancelPreviewClip"
          >
            Cancel
          </v-btn>
        </div>

        <div
          v-if="previewClipStatus === 'failed'"
          class="caption error--text mb-3"
        >
          Preview clip generation failed.
        </div>

        <!-- Preview player -->
        <div
          v-if="localValue.preview_clip"
          class="vimeo-video-field__preview-player mb-3"
        >
          <video
            :src="localValue.preview_clip"
            controls
            loop
            muted
            class="vimeo-video-field__video"
          ></video>
          <div
            v-if="localValue.preview_clip_status === 'custom'"
            class="caption grey--text mt-1"
          >
            Custom upload
          </div>
        </div>

        <!-- Custom upload section -->
        <div v-if="showCustomSection" class="vimeo-video-field__custom mt-3">
          <div v-if="!hasEntity" class="caption warning--text mb-2">
            Save the video first to upload a custom preview clip.
          </div>
          <div v-else>
            <div class="caption grey--text mb-2">
              Upload a video file or enter an external URL
            </div>

            <div class="d-flex align-center mb-2" style="gap: 8px">
              <v-file-input
                v-model="customFile"
                accept="video/*"
                label="Choose video file"
                hide-details
                dense
                outlined
                style="max-width: 300px"
              />
              <v-btn
                small
                outlined
                :loading="uploadingCustom"
                :disabled="!customFile"
                @click="uploadCustomFile"
              >
                Upload
              </v-btn>
            </div>

            <div class="d-flex align-center" style="gap: 8px">
              <v-text-field
                v-model="customUrl"
                placeholder="Or paste external video URL..."
                hide-details
                dense
                outlined
                style="max-width: 300px"
              />
              <v-btn
                small
                outlined
                :loading="uploadingCustom"
                :disabled="!customUrl"
                @click="setCustomUrl"
              >
                Set URL
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Same field editor UX as src/components/schema/builder/VimeoVideo.vue (the
// data loader `vimeo_video` schema field type), adapted to talk to the
// Video API/a saved Widget::Video id instead of a data loader entity + field key.
export default {
  name: "VimeoVideoField",
  props: {
    value: [Object, String],
    videoId: {
      type: String,
      default: null,
    },
  },
  data() {
    const normalized = this.normalizeValue(this.value);
    return {
      localValue: normalized,
      urlInput: "",
      fetchingMetadata: false,
      showCustomSection: false,
      customFile: null,
      customUrl: "",
      uploadingCustom: false,
      subscribed: false,
      generating: false,
      cancelling: false,
      clipCount: 3,
      clipDuration: 10,
      showClipOptions: false,
      cableSubscription: null,
    };
  },
  mounted() {
    const status = this.localValue?.preview_clip_status;
    if (status === "pending" || status === "processing") {
      this.subscribeToChannel();
    }
  },
  beforeDestroy() {
    this.unsubscribeFromChannel();
  },
  computed: {
    hasVideo() {
      return !!(this.localValue && this.localValue.vimeo_id);
    },
    hasEntity() {
      return !!this.videoId;
    },
    parsedUrl() {
      return this.urlInput ? this.parseVimeoUrl(this.urlInput) : null;
    },
    thumbnail() {
      const thumbs = this.localValue?.thumbnails;
      if (!thumbs || !thumbs.length) return null;
      return thumbs.reduce((prev, curr) =>
        curr.width > prev.width ? curr : prev
      ).url;
    },
    previewClipStatus() {
      return this.localValue?.preview_clip_status || null;
    },
    canGenerate() {
      const s = this.previewClipStatus;
      return s !== "processing" && s !== "pending" && s !== "custom";
    },
    isGenerating() {
      return (
        this.generating ||
        this.previewClipStatus === "pending" ||
        this.previewClipStatus === "processing"
      );
    },
    isUploading() {
      return this.fetchingMetadata || this.uploadingCustom;
    },
  },
  methods: {
    normalizeValue(val) {
      if (!val || typeof val === "string") return null;
      return { ...val };
    },
    parseVimeoUrl(url) {
      const patterns = [
        /vimeo\.com\/video\/(\d+)(?:\/([a-f0-9]+))?/,
        /vimeo\.com\/manage\/videos\/(\d+)(?:\/([a-f0-9]+))?/,
        /vimeo\.com\/(\d+)(?:\/([a-f0-9]+))?/,
      ];
      for (const pattern of patterns) {
        const m = url.match(pattern);
        if (m) return { vimeo_id: m[1], vimeo_h: m[2] || null };
      }
      return null;
    },
    formatDuration(seconds) {
      if (!seconds) return "";
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    },
    formatLoadedAt(iso) {
      if (!iso) return "";
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return new Date(iso).toLocaleDateString();
    },
    validateVimeoResponse(data) {
      const errors = [];
      if (!data.id || typeof data.id !== "string") errors.push("missing id");
      if (!data.title || typeof data.title !== "string")
        errors.push("missing title");
      if (typeof data.duration !== "number") errors.push("missing duration");
      if (!data.embed_url || typeof data.embed_url !== "string")
        errors.push("missing embed_url");
      if (!data.link || typeof data.link !== "string")
        errors.push("missing link");
      if (!Array.isArray(data.thumbnails) || data.thumbnails.length === 0) {
        errors.push("thumbnails must be a non-empty array");
      } else {
        const t = data.thumbnails[0];
        if (
          typeof t.width !== "number" ||
          typeof t.height !== "number" ||
          typeof t.url !== "string"
        )
          errors.push("thumbnail missing width/height/url");
      }
      if (!Array.isArray(data.files) || data.files.length === 0) {
        errors.push("files must be a non-empty array");
      }
      return errors;
    },
    async loadVideo() {
      const parsed = this.parsedUrl;
      if (!parsed) return;
      this.fetchingMetadata = true;
      try {
        const { data } = await this.$api.VimeoOutgate.getVideo({
          vimeo_id: parsed.vimeo_id,
          h: parsed.vimeo_h,
        });
        const errors = this.validateVimeoResponse(data);
        if (errors.length) {
          this.$alertWindow(
            `Vimeo response invalid: ${errors.join(", ")}`,
            "error"
          );
          return;
        }
        this.localValue = {
          vimeo_id: String(data.id),
          vimeo_h: parsed.vimeo_h,
          link: data.link,
          title: data.title,
          duration: data.duration,
          embed_url: data.embed_url,
          thumbnails: data.thumbnails,
          files: data.files,
          vimeo_loaded_at: new Date().toISOString(),
          preview_clip: null,
          preview_clip_status: null,
        };
        this.urlInput = "";
        this.$emit("input", { ...this.localValue });
      } catch {
        // Error shown by api interceptor
      } finally {
        this.fetchingMetadata = false;
      }
    },
    async reloadVideo() {
      if (!this.localValue?.vimeo_id) return;
      this.fetchingMetadata = true;
      try {
        const { data } = await this.$api.VimeoOutgate.getVideo({
          vimeo_id: this.localValue.vimeo_id,
          h: this.localValue.vimeo_h,
        });
        const errors = this.validateVimeoResponse(data);
        if (errors.length) {
          this.$alertWindow(
            `Vimeo response invalid: ${errors.join(", ")}`,
            "error"
          );
          return;
        }
        this.localValue = {
          ...this.localValue,
          link: data.link,
          title: data.title,
          duration: data.duration,
          embed_url: data.embed_url,
          thumbnails: data.thumbnails,
          files: data.files,
          vimeo_loaded_at: new Date().toISOString(),
        };
        this.$emit("input", { ...this.localValue });
      } catch {
        // Error shown by api interceptor
      } finally {
        this.fetchingMetadata = false;
      }
    },
    clearVideo() {
      this.unsubscribeFromChannel();
      this.localValue = null;
      this.urlInput = "";
      this.$emit("input", null);
    },
    subscribeToChannel() {
      if (!this.localValue?.vimeo_id || this.subscribed) return;
      this.subscribed = true;
      this.cableSubscription = this.$cable._cable.subscriptions.create(
        {
          channel: "Admin::VimeoPreviewClipChannel",
          token: this.authToken,
          context: this.currentUser.active_project_env,
          vimeo_id: this.localValue.vimeo_id,
        },
        {
          received: (data) => this.handleChannelData(data),
        }
      );
    },
    unsubscribeFromChannel() {
      if (this.subscribed) {
        this.cableSubscription?.unsubscribe();
        this.cableSubscription = null;
        this.subscribed = false;
      }
    },
    handleChannelData({ vimeo_id, status, preview_clip }) {
      if (!this.localValue) return;
      if (vimeo_id && vimeo_id !== this.localValue.vimeo_id) return;
      if (status === "done" && preview_clip) {
        this.localValue = {
          ...this.localValue,
          preview_clip,
          preview_clip_status: "done",
        };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      } else if (status === "failed") {
        this.localValue = {
          ...this.localValue,
          preview_clip_status: "failed",
        };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      } else if (status === "cancelled") {
        this.localValue = {
          ...this.localValue,
          preview_clip_status: null,
        };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      } else {
        this.localValue = { ...this.localValue, preview_clip_status: status };
        this.$emit("input", { ...this.localValue });
      }
    },
    async cancelPreviewClip() {
      if (!this.hasEntity || !this.localValue?.vimeo_id) return;
      this.cancelling = true;
      try {
        await this.$api.Video.vimeoPreviewCancel({ id: this.videoId });
        this.localValue = { ...this.localValue, preview_clip_status: null };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      } catch {
        // Error shown by api interceptor
      } finally {
        this.cancelling = false;
      }
    },
    async generatePreviewClip() {
      if (!this.localValue?.vimeo_id) return;
      this.generating = true;
      this.localValue = { ...this.localValue, preview_clip_status: "pending" };
      this.$emit("input", { ...this.localValue });
      this.subscribeToChannel();

      try {
        if (this.hasEntity) {
          await this.$api.Video.vimeoPreview({
            id: this.videoId,
            vimeo_id: this.localValue.vimeo_id,
            vimeo_h: this.localValue.vimeo_h,
            clip_count: this.clipCount,
            clip_duration: this.clipDuration,
          });
        } else {
          await this.$api.VimeoOutgate.createPreviewClip({
            vimeo_id: this.localValue.vimeo_id,
            vimeo_h: this.localValue.vimeo_h,
            clip_count: this.clipCount,
            clip_duration: this.clipDuration,
          });
        }
      } catch {
        this.localValue = {
          ...this.localValue,
          preview_clip_status: "failed",
        };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      } finally {
        this.generating = false;
      }
    },
    async revertPreviewClip() {
      const confirmed = await this.$confirmWindow({
        title: "Revert to generated preview?",
        description:
          "This will replace your custom preview with an auto-generated clip.",
      });
      if (!confirmed) return;
      this.localValue = { ...this.localValue, preview_clip_status: "pending" };
      this.$emit("input", { ...this.localValue });
      this.subscribeToChannel();
      try {
        await this.$api.Video.vimeoPreview({
          id: this.videoId,
          vimeo_id: this.localValue.vimeo_id,
          vimeo_h: this.localValue.vimeo_h,
          force: true,
        });
      } catch {
        this.localValue = {
          ...this.localValue,
          preview_clip_status: "failed",
        };
        this.$emit("input", { ...this.localValue });
        this.unsubscribeFromChannel();
      }
    },
    async uploadCustomFile() {
      if (!this.customFile || !this.hasEntity) return;
      this.uploadingCustom = true;
      try {
        const { data: video } = await this.$api.Video.vimeoFieldPreview({
          id: this.videoId,
          file: this.customFile,
        });
        if (video?.vimeo_data) {
          this.localValue = { ...this.localValue, ...video.vimeo_data };
          this.$emit("input", { ...this.localValue });
        }
        this.showCustomSection = false;
        this.customFile = null;
      } catch {
        // Error shown by api interceptor
      } finally {
        this.uploadingCustom = false;
      }
    },
    async setCustomUrl() {
      if (!this.customUrl || !this.hasEntity) return;
      this.uploadingCustom = true;
      try {
        const { data: video } = await this.$api.Video.vimeoFieldPreview({
          id: this.videoId,
          preview_clip_url: this.customUrl,
        });
        if (video?.vimeo_data) {
          this.localValue = { ...this.localValue, ...video.vimeo_data };
          this.$emit("input", { ...this.localValue });
        }
        this.showCustomSection = false;
        this.customUrl = "";
      } catch {
        // Error shown by api interceptor
      } finally {
        this.uploadingCustom = false;
      }
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        const normalized = this.normalizeValue(newVal);
        if (JSON.stringify(normalized) !== JSON.stringify(this.localValue)) {
          this.localValue = normalized;
          const status = normalized?.preview_clip_status;
          if (
            (status === "pending" || status === "processing") &&
            !this.subscribed
          ) {
            this.subscribeToChannel();
          }
        }
      },
    },
    isUploading(val) {
      this.$emit("uploading-changed", val);
    },
  },
};
</script>

<style scoped lang="scss">
.vimeo-video-field {
  &__thumbnail-wrap {
    position: relative;
    flex-shrink: 0;
    width: 160px;
  }

  &__thumbnail {
    width: 160px;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
    display: block;
  }

  &__thumbnail-info {
    position: absolute;
    top: 5px;
    right: 5px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff !important;
    border-radius: 50%;
    padding: 2px;
    cursor: default;
  }

  &__video {
    max-width: 400px;
    width: 100%;
    border-radius: 8px;
  }

  &__preview-section {
    border-top: 1px solid var(--v-border-base, #e0e0e0);
    padding-top: 16px;
  }

  &__clip-options {
    background: var(--v-sheet-base, #f5f5f5);
    border-radius: 6px;
    padding: 10px 12px;
    display: inline-block;
  }

  &__custom {
    border: 1px dashed var(--v-border-base, #e0e0e0);
    border-radius: 8px;
    padding: 12px;
  }
}
</style>
